import * as React from 'react';

import { Layout, Alert } from 'antd';

import { useUser } from '../User/User';
import { getPermissions } from './getPermissions';

import { PermissionsContextType, PermissionsCheckProps } from './types';

const defaultPermissionCheckContext = {
  hasPermission: () => true,
  notAllowedContent: null,
};

type PermissionsProviderProps = {
  children: React.ReactNode;
};

export const PermissionsContext = React.createContext<PermissionsContextType>(
  defaultPermissionCheckContext,
);

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({
  children,
}) => {
  const [permissions, setPermissions] = React.useState<{
    [key: string]: Array<String>;
  }>({});

  const { user, scope } = useUser();

  React.useEffect(() => {
    // Le rôle client `member` dépend du périmètre (Réseaux ou communautés) :
    // attendre /me/groups/managed pour ne pas ouvrir puis refermer le menu.
    if (user?.customer_role?.name === 'member' && !scope.loaded) return;

    const rolePermissions = getPermissions(
      user?.customer?.type,
      user?.customer_role?.name,
      scope.mode,
    );

    if (rolePermissions) {
      setPermissions(rolePermissions);
    }
  }, [user, scope]);

  const notAllowedContent = (
    <Layout className="ant-layout-content">
      <Alert
        message="Erreur"
        description="Vous ne disposez pas des droits nécessaires pour afficher ce contenu"
        type="error"
        showIcon
      />
    </Layout>
  );

  const hasPermission = ({ page, permission }: PermissionsCheckProps) => {
    if (permissions) {
      if (
        permission &&
        permissions[page] &&
        permissions[page].includes(permission)
      ) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  };

  return (
    <PermissionsContext.Provider value={{ hasPermission, notAllowedContent }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => React.useContext(PermissionsContext);
